/**
 * Client-drawer searches: Progress notes (ehr#210) and Files (ehr#209). Both run over lists
 * the drawer has already loaded. The progress-notes list arrives with each note's decrypted
 * body, so a note matches on its text without another API call. A file matches on its name,
 * type and upload date; its contents (encrypted PDFs, images, documents) are not searched.
 *
 * Search text is split into words and every word must appear, in any order and any case.
 */

import { noteDate } from './noteDate.js';

export interface NoteQuery {
  /** ISO 'YYYY-MM-DD' or '' (no lower bound). */
  from: string;
  /** ISO 'YYYY-MM-DD' or '' (no upper bound). */
  to: string;
  /** Words to find in the note; '' = any. */
  text: string;
}
export const EMPTY_NOTE_QUERY: NoteQuery = { from: '', to: '', text: '' };

export const terms = (text: string): string[] => text.toLowerCase().split(/\s+/).filter(Boolean);

export const isActiveNoteQuery = (q: NoteQuery): boolean =>
  Boolean(q.from || q.to || terms(q.text).length);

/** From after To: swap them, so a reversed range still means what the user meant. */
export const normalizeNoteQuery = (q: NoteQuery): NoteQuery =>
  q.from && q.to && q.from > q.to
    ? { ...q, from: q.to, to: q.from, text: q.text.trim() }
    : { ...q, text: q.text.trim() };

/** The local calendar day of an ISO timestamp, as the tables print it ('' if invalid). */
export function localDayKey(iso: string | Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 'YYYY-MM-DD' → 'MM/DD/YYYY', as the tables print dates ('' stays ''). */
export const printDay = (key: string): string =>
  key ? `${key.slice(5, 7)}/${key.slice(8, 10)}/${key.slice(0, 4)}` : '';

/** The search in words, for the bar above the results. */
export function describeNoteQuery(query: NoteQuery): string {
  const q = normalizeNoteQuery(query);
  const parts: string[] = [];
  if (q.from && q.to)
    parts.push(
      q.from === q.to ? `On ${printDay(q.from)}` : `From ${printDay(q.from)} to ${printDay(q.to)}`,
    );
  else if (q.from) parts.push(`On or after ${printDay(q.from)}`);
  else if (q.to) parts.push(`On or before ${printDay(q.to)}`);
  if (q.text) parts.push(`${parts.length ? 'containing' : 'Containing'} “${q.text}”`);
  return parts.join(', ');
}

/** Every piece of text in a note body (sections, Credify chips, diagnoses...), in order.
 *  Embedded files (data: URLs, e.g. a signature image) are skipped. */
export function bodyStrings(v: unknown, out: string[] = [], depth = 0): string[] {
  if (v == null || depth > 8) return out;
  if (typeof v === 'string') {
    if (v.trim() && !v.startsWith('data:')) out.push(v);
  } else if (typeof v === 'number') {
    out.push(String(v));
  } else if (Array.isArray(v)) {
    for (const x of v) bodyStrings(x, out, depth + 1);
  } else if (typeof v === 'object') {
    for (const x of Object.values(v as Record<string, unknown>)) bodyStrings(x, out, depth + 1);
  }
  return out;
}

/** A short excerpt around the first match: [before, match, after], ellipses included. */
export type Snippet = [string, string, string];
export function snippetAround(text: string, term: string, radius = 50): Snippet | null {
  const flat = text.replace(/\s+/g, ' ').trim();
  const i = flat.toLowerCase().indexOf(term.toLowerCase());
  if (i < 0 || !term) return null;
  const start = Math.max(0, i - radius);
  const end = Math.min(flat.length, i + term.length + radius);
  return [
    (start > 0 ? '…' : '') + flat.slice(start, i),
    flat.slice(i, i + term.length),
    flat.slice(i + term.length, end) + (end < flat.length ? '…' : ''),
  ];
}

export interface NoteMatch {
  hit: boolean;
  /** Where the first search word appears in the note text (null: matched on a label only,
   *  or no search text). */
  snippet: Snippet | null;
}
const MISS: NoteMatch = { hit: false, snippet: null };

/**
 * Does this progress note match? `labels` are the row's printed values (session, format,
 * clinician) so a search for a clinician's name or "DAP" finds their notes too.
 */
export function matchNote(
  note: { date: string; body: unknown },
  labels: string[],
  query: NoteQuery,
): NoteMatch {
  const q = normalizeNoteQuery(query);
  if (q.from || q.to) {
    // ehr#785: a note's date is its session day saved at midnight UTC; read as a moment it fell
    // on the evening before in US time zones, so From that day left it out. noteDate (ehr#484)
    // reads it as that day; a date with a time of day (a group session's start) stays a moment.
    const day = localDayKey(noteDate(note.date));
    if (!day || (q.from && day < q.from) || (q.to && day > q.to)) return MISS;
  }
  const words = terms(q.text);
  if (!words.length) return { hit: true, snippet: null };
  const body = bodyStrings(note.body);
  const hay = [...labels, ...body].join('\n').toLowerCase();
  if (!words.every((w) => hay.includes(w))) return MISS;
  const first = words[0]!;
  const src = body.find((s) => s.toLowerCase().includes(first));
  return { hit: true, snippet: src ? snippetAround(src, first) : null };
}

/** Does this file match the search box? Name, type word (pdf, image...), MIME type and the
 *  upload date as printed (MM/DD/YYYY). */
export function matchFile(
  file: { name: string; mime?: string | null; createdAt?: string | null },
  kind: string,
  text: string,
  /** ehr#209: more text to match on (the category's label, the note). */
  extra: (string | null | undefined)[] = [],
): boolean {
  const words = terms(text);
  if (!words.length) return true;
  const printed = printDay(file.createdAt ? localDayKey(file.createdAt) : '');
  const hay = [file.name, kind, file.mime ?? '', printed, ...extra.map((x) => x ?? '')]
    .join('\n')
    .toLowerCase();
  return words.every((w) => hay.includes(w));
}
